import { isString } from 'lodash';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBListRow, MBListRowProps } from '@shared/components/list/row';
import { Colors, bounceIn } from '@shared/styles';

export interface MBSelectableRowProps extends MBListRowProps {
  /*
        Is the row currently selected
    */
  selected?: boolean;

  /*
        If set, the selectable dot will be shown as a
        radio button (filled circle). Otherwise a checkmark
        will be shown. Default is true.
    */
  radio?: boolean;

  radioStyle?: StyleProp<ViewStyle>;

  /*
        Callback for when the row is pressed and thus selected/deselected
    */
  onChange?: (selected: boolean) => void;
}

export function MBSelectableRow({
  selected,
  title,
  onChange,
  onPress,
  radio = true,
  radioStyle,
  ...otherProps
}: MBSelectableRowProps) {
  const dotTitle = isString(title) ? title : undefined;
  const handlePress = React.useCallback(
    () => {
      onChange?.(!selected);
      onPress?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, onPress],
  );

  return (
    <MBListRow
      onPress={handlePress}
      icon={
        <MBView fill center style={radioStyle}>
          <SelectableRadioDot selected={selected} radio={radio} title={dotTitle} />
        </MBView>
      }
      title={title}
      {...otherProps}
    />
  );
}

interface SelectableRadioDotProps {
  selected?: boolean;
  radio?: boolean;
  size?: number;
  testID?: string;
  title?: string;
}

/*
    A simple radio button dot with different appearance depending on if it's selected or not
*/
export function SelectableRadioDot({
  selected,
  size,
  radio = true,
  testID,
  title,
}: SelectableRadioDotProps) {
  const role = radio ? 'radio' : 'checkbox';
  const color = selected ? Colors.v2.green : Colors.v2.green30;
  const radioIcon = selected ? 'radio-button' : 'unfilled-circle';
  const checkboxIcon = selected ? 'check-box' : 'check-box-empty';
  const icon = radio ? radioIcon : checkboxIcon;

  return (
    <View
      role={role}
      aria-checked={!!selected}
      aria-label={title}
      className={selected && bounceIn}
      key={selected ? 'selected' : 'not-selected'}
    >
      <MBIconV2 color={color} name={icon} size={size} testID={testID} />
    </View>
  );
}
